import { useState } from 'react';
import { AlertTriangle, CheckCircle, X } from 'lucide-react';
import { PageHeader } from '../PageHeader';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { usePermissions } from '../../../hooks/prioritization/usePermissions';
import { driftSeverityColors } from '../../../styles/prioritization/roleTokens';
import { Link } from 'react-router-dom';

export function DriftDetection() {
  const [resolvingEventId, setResolvingEventId] = useState<string | null>(null);
  const [resolutionNote, setResolutionNote] = useState('');
  
  const itemPools = useItemPoolsStore((state) => state.itemPools);
  const driftEvents = useItemPoolsStore((state) => state.driftEvents);
  const resolveDriftEvent = useItemPoolsStore((state) => state.resolveDriftEvent);
  const setActiveItemPool = useItemPoolsStore((state) => state.setActiveItemPool);
  const getItemById = useItemsStore((state) => state.getItemById);
  const addNotification = useUIStore((state) => state.addNotification);
  const permissions = usePermissions();
  
  const unresolvedDrift = driftEvents.filter(e => !e.resolved);
  const resolvedDrift = driftEvents.filter(e => e.resolved);
  
  const handleResolve = (eventId: string, note: string) => {
    if (!note.trim()) {
      addNotification({
        type: 'error',
        title: 'Note Required',
        message: 'Please enter a resolution note',
        duration: 3000
      });
      return;
    }
    resolveDriftEvent(eventId, note.trim());
    setResolvingEventId(null);
    setResolutionNote('');
    addNotification({
      type: 'success',
      title: 'Drift Event Resolved',
      message: 'The drift event has been marked as resolved',
      duration: 3000
    });
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={<AlertTriangle className="h-4 w-4 text-[var(--coral)]" />}
        iconBg="bg-[var(--coral-dim)]"
        title="Drift Detection"
        subtitle="Monitor when active work diverges from published priority lists"
      />
      
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <p className="text-sm text-[var(--text-sec)]">Total Drift Events</p>
          <p className="text-3xl font-bold text-[var(--text-pri)]">{driftEvents.length}</p>
        </div>
        <div className="card border-[var(--coral)] bg-[var(--coral-dim)]">
          <p className="text-sm text-[var(--coral)] font-medium">Unresolved</p>
          <p className="text-3xl font-bold text-[var(--coral)]">{unresolvedDrift.length}</p>
        </div>
        <div className="card border-[var(--green)] bg-[var(--green-dim)]">
          <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] font-medium">Resolved</p>
          <p className="text-3xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{resolvedDrift.length}</p>
        </div>
      </div>
      
      {/* Unresolved Drift Events */}
      {unresolvedDrift.length > 0 ? (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">
              Unresolved Drift Events ({unresolvedDrift.length})
            </h2>
          </div>
          
          <div className="space-y-3">
            {unresolvedDrift.map((event) => {
              const list = itemPools.find(l => l.id === event.itemPoolId);
              // Fallback to 'medium' colors if severity is not recognized
              const severityColor = driftSeverityColors[event.severity as keyof typeof driftSeverityColors] || driftSeverityColors.medium;
              
              return (
                <div
                  key={event.id}
                  className={`p-4  border ${severityColor.border} ${severityColor.bg}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className={`h-5 w-5 ${severityColor.text}`} />
                        <span className={`text-xs font-medium px-2 py-1 rounded uppercase ${severityColor.border} ${severityColor.text}`}>
                          {event.severity || 'unknown'} Severity
                        </span>
                        <span className={`text-xs font-medium px-2 py-1 rounded bg-[var(--hover-tint)] text-[var(--text-sec)]`}>
                          {(event.type || 'unknown').replace('-', ' ')}
                        </span>
                      </div>
                      
                      <h3 className={`font-medium ${severityColor.text}`}>
                        {event.description || 'No description'}
                      </h3>
                      
                      {list && (
                        <p className="text-sm text-[var(--text-sec)] mt-2">
                          Priority List: <Link 
                            to="/prioritization/lists" 
                            onClick={() => setActiveItemPool(list.id)}
                            className="text-primary-600 hover:underline"
                          >
                            {list.name}
                          </Link>
                        </p>
                      )}
                      
                      <p className="text-xs text-[var(--text-ter)] mt-2">
                        Detected: {new Date(event.detectedAt).toLocaleString()}
                      </p>
                      
                      {(event.affectedPriorityIds?.length ?? 0) > 0 && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-[var(--text-sec)] mb-1">
                            Affected Priorities ({event.affectedPriorityIds?.length ?? 0}):
                          </p>
                          <div className="space-y-1">
                            {(event.affectedPriorityIds || []).slice(0, 3).map(priorityId => {
                              const candidate = getItemById(priorityId);
                              return candidate ? (
                                <p key={priorityId} className="text-xs text-[var(--text-sec)] pl-3">
                                  • {candidate.title}
                                </p>
                              ) : null;
                            })}
                            {(event.affectedPriorityIds?.length ?? 0) > 3 && (
                              <p className="text-xs text-[var(--text-ter)] pl-3">
                                ... and {(event.affectedPriorityIds?.length ?? 0) - 3} more
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {permissions.currentRole === 'list-owner' && (
                      <div className="ml-4">
                        {resolvingEventId === event.id ? (
                          <div className="flex flex-col gap-2 min-w-[300px]">
                            <textarea
                              value={resolutionNote}
                              onChange={(e) => setResolutionNote(e.target.value)}
                              placeholder="Enter resolution note..."
                              rows={3}
                              className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                              autoFocus
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleResolve(event.id, resolutionNote)}
                                className="btn btn-primary text-sm flex items-center gap-2 flex-1"
                              >
                                <CheckCircle className="h-4 w-4" />
                                Confirm Resolve
                              </button>
                              <button
                                onClick={() => {
                                  setResolvingEventId(null);
                                  setResolutionNote('');
                                }}
                                className="btn btn-secondary text-sm flex items-center gap-2"
                                title="Cancel"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setResolvingEventId(event.id)}
                            className="btn btn-primary text-sm flex items-center gap-2"
                          >
                            <CheckCircle className="h-4 w-4" />
                            Resolve
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="card border-[var(--green)] bg-[var(--green-dim)] text-center py-12">
          <CheckCircle className="h-12 w-12 mx-auto mb-3 text-[var(--green)]" />
          <p className="text-[var(--green)] dark:text-[var(--text-ter)] font-medium">No drift detected</p>
          <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] mt-1">All work is aligned with published priority lists</p>
        </div>
      )}
      
      {/* Resolved Drift Events */}
      {resolvedDrift.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
            Recently Resolved ({resolvedDrift.length})
          </h2>
          
          <div className="space-y-3">
            {resolvedDrift.slice(0, 10).map((event) => {
              const list = itemPools.find(l => l.id === event.itemPoolId);
              
              return (
                <div
                  key={event.id}
                  className="p-4 border border-[var(--rule)] bg-[var(--bg-raised)] opacity-75"
                >
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[var(--green)] flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-medium text-[var(--text-sec)]">
                        {event.description}
                      </h3>
                      
                      {list && (
                        <p className="text-sm text-[var(--text-sec)] mt-1">
                          Priority List: {list.name}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-4 mt-2 text-xs text-[var(--text-ter)]">
                        <span>Detected: {new Date(event.detectedAt).toLocaleDateString()}</span>
                        {event.resolvedAt && (
                          <span>Resolved: {new Date(event.resolvedAt).toLocaleDateString()}</span>
                        )}
                      </div>
                      
                      {event.resolutionNote && (
                        <p className="text-xs text-[var(--text-sec)] mt-2 italic">
                          "{event.resolutionNote}"
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Help Text */}
      <div className="card bg-[var(--slate-dim)] border-[var(--slate)]">
        <h3 className="font-medium text-[var(--text-sec)] mb-2">About Drift Detection</h3>
        <p className="text-sm text-[var(--text-sec)]">
          Drift occurs when teams work on items that are below the cut line or not on any Published Priority List. 
          This system helps you detect and reconcile those misalignments before they compound.
        </p>
        <div className="mt-3 text-xs text-[var(--text-sec)]">
          <p className="font-medium mb-1">Drift Types:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li><strong>Below-line work:</strong> Working on deprioritized items</li>
            <li><strong>Off-list work:</strong> Working on items not in any Published Priority List</li>
            <li><strong>Stale priority:</strong> Ranked items no longer relevant</li>
            <li><strong>Missing work:</strong> Above-line items not being worked on</li>
          </ul>
        </div>
      </div>
    </div>
  );
}






